import { useEffect, useState } from 'react'
import { useHits } from 'react-instantsearch'

import cn from 'classnames'
import type { Hit } from 'instantsearch.js'
import { isEmpty } from 'lodash'

import { DEFAULT_NEW_PRODUCT_DURATION_IN_DAYS } from 'components/_pages/search/refinement-by-query/constants'
import { checkIsNewPublished } from 'components/_pages/search/search-page-configure/utils'
import { RegularLockDetailed } from 'components/icon/regular'
import ImageComponent from 'components/image'
import Link from 'components/link'
import BlurImage from 'components/product-card/blur-image'
import ProductTag from 'components/product-tag'
import SkeletonLoading from 'components/skeleton-loading'
import { usePutFavouriteProduct } from 'features/retailer/favourites/hooks/use-put-favourite-product'

import { useTranslate } from 'hooks'
import { ProductPricesInfos } from 'hooks/common/use-batch-products-prices'
import { useProductSearchSettings } from 'hooks/product/use-product-search-settings'
import { useNavPromo } from 'hooks/use-nav-promo'
import { useProductCategories } from 'hooks/use-product-categories'
import type { ProductCategory, ProductCategoryMapByKey } from 'hooks/use-product-categories'

import { hasDiscount } from 'utils/product'

import { LOCAL_STORAGE_KEY } from 'constants/localStorage'
import { PRODUCT_CARD_IMAGE_ASPECT_RATIO } from 'constants/product'
import { ROUTES } from 'constants/routes'

import css from '../../product-card/product-card.module.scss'
import { convertIndexLang, getCategoryURL } from '../autocomplete/utils'
import FavouriteButton from '../favourite-button'
import { ProductItem } from './type'

type HitComponent = { hit: Hit<ProductItem> }

type HitProductCardProps = {
  isBrandPreview?: boolean
  size?: 'md' | 'auto'
  className?: string
  openSignUp?: () => void
  productPrices?: ProductPricesInfos
  isLoggedIn: boolean
  isFavourite?: boolean
} & HitComponent

const getCategoryHierarchiesById = (id: string, categoryMayById: ProductCategoryMapByKey) => {
  let currCategoryId: string | undefined | null = id
  const categories: ProductCategory[] = []

  while (currCategoryId) {
    const currCategory = categoryMayById[currCategoryId] as ProductCategory | null

    if (currCategory) {
      categories.unshift(currCategory)
    }

    currCategoryId = currCategory?.parentId
  }

  return categories
}

export const HitProductCardSkeleton = ({ size = 'md', className }: Partial<HitProductCardProps>) => {
  return (
    <div className={cn([css.container, css[`container--${size}`], className])}>
      <SkeletonLoading className={css.skeletonLoadingTop} />
      <SkeletonLoading className={css.skeletonLoadingMid1} />
      <SkeletonLoading className={css.skeletonLoadingMid2} />
      <SkeletonLoading className={css.skeletonLoadingBottom} />
    </div>
  )
}

const HitProductCard = ({
  openSignUp,
  isBrandPreview = false,
  hit,
  size = 'md',
  className,
  productPrices,
  isLoggedIn,
  isFavourite: _isFavourite = false,
}: HitProductCardProps) => {
  const { t, lang } = useTranslate('common')
  const { sendEvent } = useHits()
  const { data: categoriesResult } = useProductCategories(lang)
  const { data: productSearchSettings } = useProductSearchSettings()

  const isNew = checkIsNewPublished(
    hit.attributes['first-publish-date-unix'],
    productSearchSettings?.newProductDurationInDays ?? DEFAULT_NEW_PRODUCT_DURATION_IN_DAYS
  )
  const wholeSalePrice = productPrices?.prices.wholesalePrice
  const discountedPrice = productPrices?.prices.discountedPrice
  const msrp = productPrices?.prices.msrp ?? '---'

  const categoriesStringEN = hit.categories ? hit.categories[convertIndexLang('en-HK')].lvl2 : []
  const categoryNameListEN = isEmpty(categoriesStringEN) ? [] : categoriesStringEN[0].split(' > ')

  let categoryNameList: string[] = []

  const primaryProductType = hit.attributes['primary-product-type'] as { id: string; type: string } | undefined

  if (primaryProductType?.id && categoriesResult?.categoryMapById) {
    const categoryHierarchies = getCategoryHierarchiesById(primaryProductType.id, categoriesResult.categoryMapById)
    categoryNameList = categoryHierarchies.map((category) => category.name)
  }

  const [isFavourite, setIsFavourite] = useState<boolean>(_isFavourite)
  const { mutateAsync: setFavouriteProduct } = usePutFavouriteProduct(
    () => setIsFavourite(!isFavourite),
    () => null
  )

  useEffect(() => {
    setIsFavourite(_isFavourite)
  }, [_isFavourite])

  const isDiscount = hasDiscount(wholeSalePrice, discountedPrice)

  const productDetailPagePath = isBrandPreview ? '#/' : ROUTES.PRODUCT.replace('[key]', productPrices?.key ?? '#')
  const brandPagePath = isBrandPreview
    ? '#/'
    : ROUTES.BRAND.replace('[brandKey]', hit.attributes['brand-key'] as string)

  const promotion = useNavPromo(hit.attributes['brand-key'] as string)

  return (
    <div className={cn(css.container, css[`container--${size}`], className)} data-test-id="hit-product-card">
      <div className={css.cardTopWrapper}>
        <div className={css.imageContainer}>
          <div className={css.heart}>
            <FavouriteButton
              isSolid={isFavourite}
              onToggle={
                isLoggedIn
                  ? () => {
                      void setFavouriteProduct(productPrices?.key ?? '')

                      !isFavourite &&
                        sendEvent('click', hit, 'product_favourite_add', {
                          productCardName: hit.name['en-US'],
                        })
                    }
                  : openSignUp
              }
            />
          </div>
          <ProductTag
            promotion={promotion}
            onSalePercentage={hit.attributes['on-sale-percentage']}
            isBestSeller={hit.attributes['best-seller'] as boolean}
            isNew={isNew}
            className={css.tag}
          />
          <Link href={productDetailPagePath} target="_blank" disabled={!productPrices?.key} className={css.imageLink}>
            <ImageComponent
              src={hit.images && !isEmpty(hit.images) ? hit.images[0] : '#'}
              className={css.image}
              blurDataURL={BlurImage}
              placeholder="blur"
              alt={hit.name['en-US'] + t('a11y.image')}
              objectFit="cover"
              width={292 * PRODUCT_CARD_IMAGE_ASPECT_RATIO}
              height={292}
              priority={true}
              onClick={() => {
                hit.__queryID && localStorage.setItem(LOCAL_STORAGE_KEY.ALGOLIA_QUERY_ID, hit.__queryID)
                sendEvent('click', hit, 'product_card_click', {
                  productCardName: hit.name['en-US'],
                })
              }}
            />
          </Link>
        </div>
      </div>

      <div className={css.cardBottomWrapper}>
        <div className={css.productNames}>
          {!!hit.attributes['brand-key'] && (
            <Link className={css.brand} href={brandPagePath}>
              {hit.attributes['brand-name']}
            </Link>
          )}
          <Link
            className={css.name}
            href={productDetailPagePath}
            disabled={!productPrices?.key}
            onClick={() => {
              sendEvent('click', hit, 'product_card_click', {
                productCardName: hit.name['en-US'],
              })
            }}
          >
            {hit.name['en-US']}
          </Link>
          {isEmpty(productPrices) ? (
            <div className={css.productPricesSkeletons}>
              <SkeletonLoading width="60%" />
              <SkeletonLoading width="40%" />
            </div>
          ) : (
            <>
              <p className={css.wholeSalePriceWrapper} data-test-id="unlocked-wholesale-price">
                {!!wholeSalePrice && (
                  <span>
                    <span className={css[isDiscount ? 'oldPrice' : 'price']}> {wholeSalePrice}</span>
                  </span>
                )}
                {!!wholeSalePrice && !!isDiscount && <span className={css.discountedPrice}>{discountedPrice}</span>}
              </p>
              {!wholeSalePrice && (
                <button
                  className={css.unlockWholesalePriceWrapper}
                  onClick={openSignUp}
                  data-test-id="btn-unlock-wholesale-price"
                >
                  <p className={css.unlockWholesalePrice}>{t('product_card.unlock_wholesale_price')}</p>
                  <RegularLockDetailed />
                </button>
              )}
              <p className={css.msrp}>
                {msrp} {t('product_card.suggest_retail')}
              </p>
            </>
          )}
        </div>
        <div className={css.productPrices}>
          <Link href={isBrandPreview ? '#/' : `${getCategoryURL(categoryNameListEN)}`} className={css.subCategory}>
            {categoryNameList[categoryNameList.length - 1]}
          </Link>
        </div>
      </div>
    </div>
  )
}

export default HitProductCard
